/**
 * Billing (Joe, 28 Oct 2026) — every charge in one list, handled the same way:
 * trade orders, duty re-charges, storage and fulfilment. Worked out
 * automatically; nothing goes to Xero until someone sends it.
 *
 *   To invoice   everything waiting (monthly lines show "Month running")
 *   Sent         by month: Draft in Xero / Approved in Xero / Marked invoiced
 *
 * Every line: Open (what's behind it), Mark invoiced, Send to Xero, Undo.
 * See services/commercial/billingService.js.
 */
import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiFetch, getToken } from '../hooks/useApi.js';
import { uiAlert, uiConfirm } from '../utils/dialogs.js';

const GREEN = 'var(--pld-green)';
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const gbp = (n) => `£${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TYPES = [
  { key: '', label: 'All' },
  { key: 'trade', label: 'Trade orders' },
  { key: 'fulfilment', label: 'Fulfilment' },
  { key: 'storage', label: 'Storage' },
  { key: 'duty', label: 'Duty re-charges' },
];

const STATUS = {
  waiting:       ['Waiting', '#fdf0c8', '#7a4a06'],
  month_running: ['Month running', '#dde8fb', '#1d3f8f'],
  not_sent:      ['Not sent', '#fde2e2', '#8f1d1d'],
  draft:         ['Draft in Xero', '#d9f2e1', '#14532d'],
  approved:      ['Approved in Xero', '#e7e1f7', '#4b2a8f'],
  marked:        ['Marked invoiced', '#ecece6', '#3f3f3a'],
  paid:          ['Paid', '#e7e1f7', '#4b2a8f'],
};
const canSend = (l) => ['waiting', 'month_running', 'not_sent'].includes(l.status);
const canUndo = (l) => ['draft', 'marked'].includes(l.status) || (l.status === 'not_sent' && l.key.startsWith('fbill:'));

function Btn({ children, variant = 'primary', ...props }) {
  const v = variant === 'primary'
    ? { background: GREEN, color: '#fff', border: 'none' }
    : { background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)' };
  return <button type="button" {...props} style={{ minHeight: 34, padding: '0 12px', borderRadius: 'var(--radius)', fontSize: 13, fontWeight: 600, cursor: props.disabled ? 'default' : 'pointer', opacity: props.disabled ? 0.5 : 1, whiteSpace: 'nowrap', ...v, ...props.style }}>{children}</button>;
}
function Pill({ status }) {
  const [label, bg, fg] = STATUS[status] || [status, '#eee', '#333'];
  return <span style={{ display: 'inline-block', padding: '3px 9px', borderRadius: 99, fontSize: 12, fontWeight: 600, background: bg, color: fg, whiteSpace: 'nowrap' }}>{label}</span>;
}
const th = { textAlign: 'left', padding: '9px 12px', fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-muted)' };
const td = { padding: '10px 12px', verticalAlign: 'top' };

function monthOptions() {
  const out = []; const d = new Date(); d.setDate(1);
  for (let i = 0; i < 12; i++) { out.push({ value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, label: `${MONTHS[d.getMonth()]} ${d.getFullYear()}` }); d.setMonth(d.getMonth() - 1); }
  return out;
}

async function download(path) {
  const res = await fetch(`/api${path}`, { headers: { Authorization: `Bearer ${getToken()}` } });
  if (!res.ok) throw new Error('Could not download the statement');
  const cd = res.headers.get('Content-Disposition') || '';
  const name = decodeURIComponent((cd.match(/filename="([^"]+)"/) || [])[1] || 'statement.csv');
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a'); a.href = url; a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

// ── The actions, the same for every line ──────────────────────────────────────
const path = (key, act) => `/billing/lines/${encodeURIComponent(key)}${act ? `/${act}` : ''}`;
async function sendLine(l, { ask = true } = {}) {
  if (ask && l.status === 'month_running' && !(await uiConfirm(`${l.for} isn't over yet — orders despatched later this month will go on a second bill. Send ${l.who}'s bill now anyway?`, { okLabel: 'Send now' }))) return false;
  await apiFetch(path(l.key, 'send'), { method: 'POST', body: {} });
  return true;
}
async function markLine(l) {
  if (!(await uiConfirm(`Mark ${l.who} — ${l.what.toLowerCase()} ${gbp(l.amount)} as invoiced outside Xero? It comes off To invoice.`, { okLabel: 'Mark invoiced' }))) return false;
  await apiFetch(path(l.key, 'mark'), { method: 'POST', body: {} });
  return true;
}
async function undoLine(l) {
  const xero = l.status === 'draft' ? ' Its draft invoice in Xero will be deleted.' : '';
  if (!(await uiConfirm(`Undo ${l.who} — ${l.what.toLowerCase()} ${gbp(l.amount)}? It goes back to To invoice.${xero}`, { okLabel: 'Undo' }))) return false;
  await apiFetch(path(l.key, 'undo'), { method: 'POST', body: {} });
  return true;
}

function LineActions({ l, xero, busy, run, onOpen }) {
  return (
    <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'nowrap', justifyContent: 'flex-end' }}>
      {onOpen && <Btn variant="ghost" onClick={onOpen}>Open</Btn>}
      {canSend(l) && <Btn variant="ghost" disabled={busy} onClick={() => run(() => markLine(l))}>Mark invoiced</Btn>}
      {canSend(l) && <Btn disabled={busy || !xero} title={xero ? '' : 'Xero isn\'t connected'} onClick={() => run(() => sendLine(l))}>{l.status === 'not_sent' ? 'Retry Xero' : 'Send to Xero'}</Btn>}
      {canUndo(l) && <Btn variant="ghost" disabled={busy} onClick={() => run(() => undoLine(l))}>Undo</Btn>}
    </span>
  );
}

// ── A line opened ─────────────────────────────────────────────────────────────
function LineDetail({ line, xero, onBack }) {
  const [d, setD] = useState(null); const [err, setErr] = useState(''); const [busy, setBusy] = useState(false);
  useEffect(() => { apiFetch(path(line.key)).then((r) => setD(r.data ?? r)).catch((e) => setErr(e.message)); }, [line.key]);
  const run = async (fn) => { setBusy(true); setErr(''); try { if (await fn()) onBack(true); } catch (e) { setErr(e.message); } finally { setBusy(false); } };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <button type="button" onClick={() => onBack(false)} style={{ alignSelf: 'flex-start', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: GREEN, fontWeight: 600, fontSize: 14 }}>← Back</button>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'flex-start' }}>
        <div style={{ flex: '1 1 320px' }}>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 600 }}>{d?.kind || line.what}</div>
          <h2 style={{ margin: '2px 0 6px', fontSize: 21, fontWeight: 700 }}>{d?.title || `${line.who} — ${line.for}`}</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 13, color: 'var(--text-muted)' }}>
            <Pill status={line.status} />{line.invoice_number && <span>{line.invoice_number}</span>}{d?.sub && <span>{d.sub}</span>}
          </div>
          {line.error && <p style={{ margin: '8px 0 0', fontSize: 13, color: '#8f1d1d' }}>{line.error}</p>}
        </div>
        <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {d?.csv && <Btn variant="ghost" onClick={() => download(d.csv).catch((e) => setErr(e.message))}>Statement (CSV)</Btn>}
          <LineActions l={line} xero={xero} busy={busy} run={run} />
        </span>
      </div>
      {err && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{err}</p>}
      {!d ? (!err && <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading…</p>) : (<>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10 }}>
          {d.tiles.map((t) => (
            <div key={t.label} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '11px 14px' }}>
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>{t.label}</div>
              <div style={{ fontSize: 17, fontWeight: 700, marginTop: 3 }}>{t.value}</div>
            </div>
          ))}
        </div>
        <div style={{ overflowX: 'auto', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ background: 'var(--surface-2)' }}>{d.columns.map((c, i) => <th key={c} style={{ ...th, textAlign: d.align?.[i] === 'r' ? 'right' : 'left' }}>{c}</th>)}</tr></thead>
            <tbody>
              {d.rows.length === 0 && <tr><td colSpan={d.columns.length} style={{ ...td, color: 'var(--text-muted)' }}>Nothing to show.</td></tr>}
              {d.rows.map((r, i) => <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>{r.map((v, j) => <td key={j} style={{ ...td, textAlign: d.align?.[j] === 'r' ? 'right' : 'left' }}>{v}</td>)}</tr>)}
            </tbody>
          </table>
        </div>
      </>)}
    </div>
  );
}

// ── The list ──────────────────────────────────────────────────────────────────
export default function Billing() {
  const [params, setParams] = useSearchParams();
  const view = ['sent', 'terms'].includes(params.get('tab')) ? params.get('tab') : 'to_invoice';
  const months = monthOptions();
  const month = params.get('month') || months[0].value;
  const [type, setType] = useState('');
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState(() => new Set());
  const [open, setOpen] = useState(null);
  const [count, setCount] = useState(null);

  const load = useCallback(async () => {
    if (view === 'terms') return;
    setErr('');
    try {
      const r = await apiFetch(`/billing/lines?view=${view}&month=${month}`); const d = r.data ?? r;
      setData(d); setPicked(new Set());
      if (view === 'to_invoice') setCount(d.lines.length);
    } catch (e) { setErr(e.message); }
  }, [view, month]);
  useEffect(() => { setData(null); load(); }, [load]);
  useEffect(() => { if (view !== 'to_invoice' && count == null) apiFetch('/billing/lines?view=to_invoice').then((r) => setCount((r.data ?? r).lines.length)).catch(() => {}); }, [view, count]);

  const setTab = (t) => { setOpen(null); setType(''); setParams(t === 'sent' ? { tab: 'sent', month } : t === 'terms' ? { tab: 'terms' } : {}); };
  const run = async (fn) => { setBusy(true); setErr(''); try { if (await fn()) await load(); } catch (e) { setErr(e.message); await load(); } finally { setBusy(false); } };

  const tabBar = (
    <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
      {[['to_invoice', 'To invoice', count], ['sent', 'Sent'], ['terms', 'Customer terms']].map(([k, label, n]) => (
        <button key={k} type="button" onClick={() => setTab(k)} style={{ padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: view === k ? 700 : 500, color: view === k ? GREEN : 'var(--text-muted)', borderBottom: view === k ? `2px solid ${GREEN}` : '2px solid transparent', marginBottom: -1 }}>
          {label}{n != null && <span style={{ fontWeight: 500, color: 'var(--text-muted)', marginLeft: 6 }}>{n}</span>}
        </button>
      ))}
    </div>
  );
  if (view === 'terms') return <Page>{tabBar}<CustomerTerms /></Page>;
  if (open) return <Page><LineDetail line={open} xero={!!data?.xero_connected} onBack={(changed) => { setOpen(null); if (changed) load(); }} /></Page>;

  const lines = data?.lines || [];
  const shown = lines.filter((l) => !type || l.type === type);
  const total = shown.reduce((a, l) => a + Number(l.amount || 0), 0);
  const chosen = shown.filter((l) => picked.has(l.key) && canSend(l));
  const toggle = (k) => setPicked((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const allPicked = shown.filter(canSend).length > 0 && shown.filter(canSend).every((l) => picked.has(l.key));

  async function bulk(kind) {
    const running = chosen.filter((l) => l.status === 'month_running');
    const what = kind === 'send' ? 'Send' : 'Mark';
    let msg = `${what} ${chosen.length} line${chosen.length === 1 ? '' : 's'} (${gbp(chosen.reduce((a, l) => a + Number(l.amount), 0))}) ${kind === 'send' ? 'to Xero as drafts' : 'as invoiced outside Xero'}?`;
    if (running.length) msg += ` ${running.length} ${running.length === 1 ? 'is a month' : 'are months'} that isn't over yet — later orders will go on a second bill.`;
    if (!(await uiConfirm(msg, { okLabel: kind === 'send' ? 'Send to Xero' : 'Mark invoiced' }))) return;
    setBusy(true); setErr('');
    const failed = [];
    for (const l of chosen) {
      try { await apiFetch(path(l.key, kind), { method: 'POST', body: {} }); } catch (e) { failed.push(`${l.who} (${l.for}): ${e.message}`); }
    }
    setBusy(false); await load();
    if (failed.length) await uiAlert(`${chosen.length - failed.length} done. Not done:\n${failed.join('\n')}`);
  }

  return (
    <Page>
      {tabBar}

      {data && !data.xero_connected && (
        <p style={{ margin: 0, padding: '10px 14px', borderRadius: 'var(--radius)', background: '#fdf0c8', color: '#7a4a06', fontSize: 13 }}>Xero isn't connected — Settings → Integrations → Connect to Xero. Until then you can mark things as invoiced.</p>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        {TYPES.map((t) => {
          const ls = lines.filter((l) => !t.key || l.type === t.key);
          const on = type === t.key;
          return (
            <button key={t.key} type="button" onClick={() => setType(t.key)} style={{ minHeight: 36, padding: '0 14px', borderRadius: 99, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: on ? 'none' : '1px solid var(--border)', background: on ? GREEN : 'var(--surface)', color: on ? '#fff' : 'var(--text)' }}>
              {t.label} <span style={{ fontWeight: 500, opacity: 0.8 }}>{ls.length}{t.key && ls.length ? ` · ${gbp(ls.reduce((a, l) => a + Number(l.amount || 0), 0))}` : ''}</span>
            </button>
          );
        })}
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {view === 'to_invoice' ? (<>
            <Btn variant="ghost" disabled={busy || !chosen.length} onClick={() => bulk('mark')}>Mark selected invoiced</Btn>
            <Btn disabled={busy || !chosen.length || !data?.xero_connected} onClick={() => bulk('send')}>Send selected to Xero{chosen.length ? ` (${chosen.length})` : ''}</Btn>
          </>) : (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600 }}>Month
              <select value={month} onChange={(e) => setParams({ tab: 'sent', month: e.target.value })} style={{ minHeight: 36, padding: '0 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', background: 'var(--surface)', fontSize: 13 }}>
                {months.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </label>
          )}
        </span>
      </div>

      {err && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{err}</p>}

      {!data ? (!err && <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading…</p>) : (
        <div style={{ overflowX: 'auto', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)' }}>
          <table style={{ width: '100%', minWidth: 1000, borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: 'var(--surface-2)' }}>
                {view === 'to_invoice' && <th style={{ ...th, width: 30 }}><input type="checkbox" aria-label="Select all" checked={allPicked} onChange={() => setPicked(allPicked ? new Set() : new Set(shown.filter(canSend).map((l) => l.key)))} /></th>}
                <th style={th}>What</th><th style={th}>Who</th><th style={th}>For</th>
                <th style={{ ...th, textAlign: 'right' }}>Amount</th><th style={th}>Status</th><th style={th}><span style={{ position: 'absolute', left: -9999 }}>Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 && <tr><td colSpan={7} style={{ ...td, padding: '28px 12px', textAlign: 'center', color: 'var(--text-muted)' }}>{view === 'to_invoice' ? 'Nothing waiting to be invoiced.' : `Nothing invoiced for ${data.label}.`}</td></tr>}
              {shown.map((l) => (
                <tr key={l.key} style={{ borderTop: '1px solid var(--border)' }}>
                  {view === 'to_invoice' && <td style={td}>{canSend(l) && <input type="checkbox" aria-label={`Select ${l.who}`} checked={picked.has(l.key)} onChange={() => toggle(l.key)} />}</td>}
                  <td style={td}><div style={{ fontWeight: 600 }}>{l.what}</div><div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{l.ref}</div></td>
                  <td style={td}>{l.who}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}><div>{l.for}</div>{l.detail && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{l.detail}</div>}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{gbp(l.amount)}</td>
                  <td style={td}><Pill status={l.status} />
                    {(l.invoice_number || l.note || l.error) && <div style={{ fontSize: 12, color: l.error ? '#8f1d1d' : 'var(--text-muted)', marginTop: 3, maxWidth: 260 }}>{l.error || l.invoice_number || l.note}</div>}
                  </td>
                  <td style={{ ...td, textAlign: 'right' }}><LineActions l={l} xero={!!data.xero_connected} busy={busy} run={run} onOpen={() => setOpen(l)} /></td>
                </tr>
              ))}
            </tbody>
            {shown.length > 0 && (
              <tfoot><tr style={{ borderTop: '1px solid var(--border)', background: 'var(--surface-2)' }}>
                {view === 'to_invoice' && <td />}
                <td colSpan={3} style={{ ...td, fontWeight: 600 }}>{shown.length} {view === 'to_invoice' ? 'waiting' : 'invoiced'}</td>
                <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{gbp(total)}</td><td colSpan={2} />
              </tr></tfoot>
            )}
          </table>
        </div>
      )}

      {view === 'to_invoice' && <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>Monthly lines (fulfilment) build up through the month and are ready on the 1st; storage is charged in advance, so it's ready from the start of the month. Something deleted or voided in Xero comes back here to send again.</p>}
    </Page>
  );
}

// ── Customer terms: payment terms, how they pay, and discounts — set here only ──
function discountText(d) {
  const v = (x) => (d.unit === 'percent' ? `${Number(x)}%` : `£${Math.abs(Number(x)).toFixed(2)}`);
  const one = (x) => (Number(x) < 0 ? `${v(x)} added` : `${v(x)} off`);
  const parts = [];
  if (Number(d.flat)) parts.push(`${one(d.flat)}${d.unit === 'percent' ? '' : ' each'}`);
  for (const t of d.tiers || []) parts.push(`${one(t.discount)} from ${t.qty}${d.tier_basis === 'category' ? ' on the order' : ''}`);
  return parts.join(', ') || 'none';
}

const fieldStyle = { minHeight: 36, padding: '0 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', background: 'var(--surface)', fontSize: 13, color: 'var(--text)' };

function OutletDiscounts({ customer, onChanged }) {
  const [list, setList] = useState(null); const [err, setErr] = useState('');
  const [items, setItems] = useState([]); const [form, setForm] = useState({ stock_item_id: '', unit: 'amount', flat: '' });
  const load = useCallback(() => apiFetch(`/billing/terms/outlet/${customer.id}/discounts`).then((r) => setList((r.data ?? r).discounts)).catch((e) => setErr(e.message)), [customer.id]);
  useEffect(() => { load(); apiFetch('/billing/terms/products').then((r) => setItems((r.data ?? r).products || [])).catch(() => {}); }, [load]);
  async function add(e) {
    e.preventDefault(); setErr('');
    const had = (list || []).find((x) => x.stock_item_id === form.stock_item_id);
    if (had && !(await uiConfirm(`${customer.name} already has ${discountText(had)} on ${had.item_name}. Replace it?`, { okLabel: 'Replace' }))) return;
    try { await apiFetch(`/billing/terms/outlet/${customer.id}/discounts`, { method: 'POST', body: form }); setForm({ stock_item_id: '', unit: 'amount', flat: '' }); await load(); onChanged(); }
    catch (er) { setErr(er.message); }
  }
  async function remove(d) {
    if (!(await uiConfirm(`Remove ${customer.name}'s discount on ${d.item_name}?`, { okLabel: 'Remove' }))) return;
    try { await apiFetch(`/billing/terms/discounts/${d.id}`, { method: 'DELETE' }); await load(); onChanged(); } catch (er) { setErr(er.message); }
  }
  return (
    <div style={{ padding: '6px 12px 14px 28px', background: 'var(--surface-2)' }}>
      {err && <p style={{ color: '#dc2626', fontSize: 13, margin: '6px 0' }}>{err}</p>}
      {!list ? <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading…</p> : list.length === 0
        ? <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '8px 0' }}>No discounts — {customer.name} pays its price list's prices.</p>
        : list.map((d) => (
          <div key={d.id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
            <span style={{ flex: '1 1 220px' }}>{d.item_name}</span>
            <span style={{ flex: '1 1 200px' }}>{discountText(d)}</span>
            <button type="button" onClick={() => remove(d)} aria-label={`Remove the discount on ${d.item_name}`} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 18 }}>&#215;</button>
          </div>
        ))}
      <form onSubmit={add} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
        <select value={form.stock_item_id} onChange={(e) => setForm((f) => ({ ...f, stock_item_id: e.target.value }))} aria-label="Product" style={{ ...fieldStyle, minWidth: 220 }}>
          <option value="">Add a discount on…</option>
          {items.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
        </select>
        <input type="number" min="0" step="0.01" value={form.flat} onChange={(e) => setForm((f) => ({ ...f, flat: e.target.value }))} placeholder="0.00" aria-label="Discount" style={{ ...fieldStyle, width: 90 }} />
        <select value={form.unit} onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))} aria-label="£ or %" style={fieldStyle}>
          <option value="amount">£ off each</option><option value="percent">% off</option>
        </select>
        <Btn type="submit" disabled={!form.stock_item_id || !form.flat}>Add</Btn>
      </form>
    </div>
  );
}

function CustomerTerms() {
  const [d, setD] = useState(null); const [err, setErr] = useState(''); const [q, setQ] = useState(''); const [open, setOpen] = useState(null);
  const load = useCallback(() => apiFetch('/billing/terms').then((r) => setD(r.data ?? r)).catch((e) => setErr(e.message)), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return err ? <p style={{ color: '#dc2626', fontSize: 13 }}>{err}</p> : <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading…</p>;
  const methodName = (m) => d.methods[m] || m;
  async function saveStandard(patch) {
    setErr('');
    try { await apiFetch('/billing/terms/standard', { method: 'PUT', body: patch }); await load(); } catch (e) { setErr(e.message); }
  }
  async function saveCustomer(c, patch) {
    setErr('');
    const body = { days: c.terms_days, method: c.payment_method, ...patch };
    try { await apiFetch(`/billing/terms/${c.kind}/${c.id}`, { method: 'PUT', body }); await load(); } catch (e) { setErr(e.message); }
  }
  const shown = d.customers.filter((c) => !q || c.name.toLowerCase().includes(q.toLowerCase()) || (c.account_ref || '').toLowerCase().includes(q.toLowerCase()));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '16px 18px', display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 260px' }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Standard terms</div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>Everyone is on these unless their own are set below. They're in the welcome email, and Xero invoices are due on them.</div>
        </div>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600 }}>Payment terms (days)
          <input type="number" min="0" max="365" defaultValue={d.standard.days} key={`sd${d.standard.days}`} onBlur={(e) => Number(e.target.value) !== d.standard.days && saveStandard({ days: e.target.value })} style={{ ...fieldStyle, width: 110 }} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600 }}>How they pay
          <select value={d.standard.method} onChange={(e) => saveStandard({ method: e.target.value })} style={fieldStyle}>
            {Object.entries(d.methods).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
      </div>

      {err && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{err}</p>}
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a customer" aria-label="Find a customer" style={{ ...fieldStyle, maxWidth: 320 }} />

      <div style={{ overflowX: 'auto', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)' }}>
        <table style={{ width: '100%', minWidth: 760, borderCollapse: 'collapse', fontSize: 14 }}>
          <thead><tr style={{ background: 'var(--surface-2)' }}>
            <th style={th}>Customer</th><th style={th}>Payment terms</th><th style={th}>How they pay</th><th style={th}>Discounts</th>
          </tr></thead>
          <tbody>
            {shown.map((c) => {
              const key = `${c.kind}:${c.id}`;
              return [
                <tr key={key} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={td}><div style={{ fontWeight: 600 }}>{c.name}</div><div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.kind === 'outlet' ? `Outlet${c.account_ref ? ` · ${c.account_ref}` : ''}` : 'Contract client'}</div></td>
                  <td style={td}>
                    <select value={c.terms_days == null ? '' : String(c.terms_days)} onChange={(e) => saveCustomer(c, { days: e.target.value === '' ? null : e.target.value === 'other' ? c.days : e.target.value })} aria-label={`${c.name} payment terms`} style={fieldStyle}>
                      <option value="">Standard ({d.standard.days} days)</option>
                      {[0, 7, 14, 30, 60].filter((n) => n !== c.terms_days).map((n) => <option key={n} value={n}>{n === 0 ? 'On order (0 days)' : `${n} days`}</option>)}
                      {c.terms_days != null && <option value={String(c.terms_days)}>{c.terms_days} days</option>}
                    </select>
                  </td>
                  <td style={td}>
                    <select value={c.payment_method || ''} onChange={(e) => saveCustomer(c, { method: e.target.value || null })} aria-label={`How ${c.name} pays`} style={fieldStyle}>
                      <option value="">Standard ({methodName(d.standard.method)})</option>
                      {Object.entries(d.methods).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </td>
                  <td style={td}>{c.kind === 'outlet'
                    ? <Btn variant="ghost" onClick={() => setOpen(open === key ? null : key)}>{c.discounts ? `${c.discounts} discount${c.discounts === 1 ? '' : 's'}` : 'None'} {open === key ? '▴' : '▾'}</Btn>
                    : <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>—</span>}</td>
                </tr>,
                open === key && <tr key={`${key}-d`}><td colSpan={4} style={{ padding: 0 }}><OutletDiscounts customer={c} onChanged={load} /></td></tr>,
              ];
            })}
          </tbody>
        </table>
      </div>
      <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>Discounts are money off a product on top of the outlet's price list. Discounts imported from Brewman show here too.</p>
    </div>
  );
}

function Page({ children }) {
  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 8px', display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Billing</h1>
        <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: 14 }}>Everything that's gone out and needs invoicing. Worked out automatically — nothing goes to Xero until you send it.</p>
      </div>
      {children}
    </div>
  );
}
